import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { Button, ErrorLine, PageHeader } from '../ui/ui.js';
import { apiFetch } from '../lib/api.js';
import { supabase } from '../lib/supabase.js';
import { useOrg } from '../app/OrgContext.js';
import { driveConfigured, pickMediaFromDrive } from '../lib/googleDrive.js';
import type { MediaItem, Creative, Connections } from '../lib/types.js';

type Stage = 'raw' | 'ready';

export function Media() {
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<MediaItem[]>([]);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [url, setUrl] = useState('');
  const [fetching, setFetching] = useState(false);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [preview, setPreview] = useState<MediaItem | null>(null);
  const [driveFolderId, setDriveFolderId] = useState<string | null>(null);

  const stage: Stage = params.get('stage') === 'ready' ? 'ready' : 'raw';
  const setStage = (s: Stage) => setParams(s === 'raw' ? {} : { stage: s });

  const load = useCallback(() => {
    if (!orgId) return;
    void apiFetch<{ media: MediaItem[] }>(`/api/orgs/${orgId}/media`)
      .then((d) => setItems(d.media))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
    void apiFetch<{ creatives: Creative[] }>(`/api/orgs/${orgId}/creatives`)
      .then((d) => setCreatives(d.creatives))
      .catch(() => {});
    void apiFetch<Connections>(`/api/orgs/${orgId}/connections`)
      .then((d) => setDriveFolderId(d.drive?.folder_id ?? null))
      .catch(() => {});
  }, [orgId]);
  useEffect(() => {
    load();
  }, [load]);

  const raw = useMemo(() => items.filter((m) => m.stage === 'raw'), [items]);
  const ready = useMemo(() => items.filter((m) => m.stage === 'ready'), [items]);
  const shown = stage === 'raw' ? raw : ready;

  async function uploadFile(file: File) {
    if (!orgId) return;
    const isMedia = file.type.startsWith('image/') || file.type.startsWith('video/');
    if (!isMedia) {
      setError('Only images and video are supported.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const { path, token } = await apiFetch<{ path: string; token: string }>(
        `/api/orgs/${orgId}/media/upload-url`,
        { method: 'POST', body: JSON.stringify({ mime: file.type }) },
      );
      const up = await supabase.storage.from('media').uploadToSignedUrl(path, token, file);
      if (up.error) throw up.error;
      await apiFetch(`/api/orgs/${orgId}/media`, {
        method: 'POST',
        body: JSON.stringify({ path, mime: file.type }),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  }

  async function fromUrl() {
    if (!orgId || !url.trim()) return;
    setFetching(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/media/from-url`, {
        method: 'POST',
        body: JSON.stringify({ url: url.trim() }),
      });
      setUrl('');
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not import from URL');
    } finally {
      setFetching(false);
    }
  }

  async function fromDrive() {
    if (!orgId) return;
    setError(null);
    try {
      const pick = await pickMediaFromDrive(driveFolderId, orgId);
      if (pick) await uploadFile(pick.file);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Google Drive import failed');
    }
  }

  async function del(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/media/${id}`, { method: 'DELETE' });
      setPreview((p) => (p?.id === id ? null : p));
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function rename(id: string, title: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/media/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      });
      setItems((l) => l.map((m) => (m.id === id ? { ...m, title } : m)));
      setPreview((p) => (p?.id === id ? { ...p, title } : p));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rename failed');
    }
  }

  // Attach a finished design to an existing post, then jump to Posts.
  async function attachToPost(m: MediaItem, creativeId: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${creativeId}`, {
        method: 'PATCH',
        body: JSON.stringify({ media_id: m.id }),
      });
      setPreview(null);
      navigate('/studio/library');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not attach');
    }
  }

  return (
    <div style={{ padding: '26px 30px 40px', animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="Your raw source material · upload or import" title="Photos & clips" />
      {error && <ErrorLine>{error}</ErrorLine>}

      {/* Stage tabs */}
      <div style={{ display: 'flex', gap: 6, margin: '4px 0 22px', flexWrap: 'wrap' }}>
        <Tab
          active={stage === 'raw'}
          onClick={() => setStage('raw')}
          label="Photos & clips"
          count={raw.length}
        />
        <Tab
          active={stage === 'ready'}
          onClick={() => setStage('ready')}
          label="Finished designs"
          count={ready.length}
        />
      </div>

      {stage === 'raw' ? (
        /* Add: upload or URL — everything you add lands here as a photo/clip. */
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 26 }}>
          <label
            onDragOver={(e) => {
              e.preventDefault();
              if (!uploading) setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) void uploadFile(f);
            }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              padding: '26px 15px',
              borderRadius: 10,
              border: `1.5px dashed ${dragOver ? C.basil : C.line}`,
              background: dragOver ? alpha(C.basil, 8) : C.surface2,
              cursor: uploading ? 'default' : 'pointer',
              color: dragOver ? C.basil : C.muted,
              fontSize: 12.5,
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 20, color: C.basil }}>↑</span>
            {uploading ? 'Uploading…' : 'Drag an image or video here, or click to browse'}
            <span style={{ fontSize: 10.5, color: C.faint }}>JPEG, PNG, WebP · MP4, MOV, WebM</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
              style={{ display: 'none' }}
              disabled={uploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void uploadFile(f);
              }}
            />
          </label>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: 10,
              padding: '20px 18px',
              borderRadius: 10,
              border: `1px solid ${C.line}`,
              background: C.surface2,
            }}
          >
            <div style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
              OR IMPORT AN IMAGE FROM A URL
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void fromUrl()}
                placeholder="https://…/image.jpg"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: C.surface,
                  border: `1px solid ${C.line}`,
                  borderRadius: 9,
                  padding: '9px 12px',
                  fontSize: 13,
                  color: C.primary,
                  fontFamily: 'inherit',
                }}
              />
              <Button
                onClick={() => void fromUrl()}
                style={{ padding: '8px 14px', fontSize: 12, opacity: fetching ? 0.6 : 1 }}
              >
                {fetching ? '…' : 'Import'}
              </Button>
            </div>
            {driveConfigured ? (
              <button
                type="button"
                onClick={() => void fromDrive()}
                disabled={uploading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '9px 14px',
                  borderRadius: 9,
                  border: `1px solid ${C.line}`,
                  background: C.surface,
                  color: C.primary,
                  fontSize: 12.5,
                  fontFamily: 'inherit',
                  cursor: uploading ? 'default' : 'pointer',
                }}
              >
                <GoogleDriveMark />
                Import from Google Drive
              </button>
            ) : (
              <div style={{ fontSize: 11, color: C.faint, lineHeight: 1.5 }}>
                Google Drive: add VITE_GOOGLE_CLIENT_ID + VITE_GOOGLE_API_KEY to enable.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 22, lineHeight: 1.6 }}>
          Finished, brand-styled designs exported from Compose. Click one to preview it and attach
          it to a post.
        </div>
      )}

      {/* Grid */}
      {shown.length === 0 ? (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>
          {stage === 'raw'
            ? 'NO PHOTOS OR CLIPS YET — upload or import above.'
            : 'NO FINISHED DESIGNS YET — style a photo in Compose and save it.'}
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill,minmax(150px,1fr))',
            gap: 12,
          }}
        >
          {shown.map((m) => (
            <div
              key={m.id}
              onClick={() => setPreview(m)}
              title="Click to preview"
              style={{
                position: 'relative',
                borderRadius: 10,
                overflow: 'hidden',
                border: `1px solid ${C.line}`,
                background: C.surface,
                cursor: 'pointer',
              }}
            >
              <Thumb m={m} height={140} />

              {/* Desire tag (raw) */}
              {m.desire && m.kind !== 'video' && <Badge>{m.desire.toUpperCase()}</Badge>}

              {/* Edit — raw images only (video editing not yet built) */}
              {stage === 'raw' && m.kind === 'image' && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/studio/create?media=${m.id}`);
                  }}
                  title="Style in Compose"
                  style={{
                    position: 'absolute',
                    bottom: 6,
                    left: 6,
                    padding: '3px 9px',
                    borderRadius: 6,
                    background: C.basil,
                    color: C.basilInk,
                    fontSize: 11,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  ✦ Edit
                </span>
              )}

              {/* Ready title */}
              {stage === 'ready' && m.title && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    padding: '14px 8px 6px',
                    background: 'linear-gradient(transparent, rgba(0,0,0,.75))',
                    color: '#fff',
                    fontSize: 11,
                    lineHeight: 1.3,
                    pointerEvents: 'none',
                  }}
                >
                  {m.title}
                </div>
              )}

              <span
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirmDel === m.id) void del(m.id);
                  else setConfirmDel(m.id);
                }}
                onMouseLeave={() => confirmDel === m.id && setConfirmDel(null)}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  padding: '2px 8px',
                  borderRadius: 6,
                  background: confirmDel === m.id ? C.act : 'rgba(0,0,0,.6)',
                  color: '#fff',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
                title="Delete"
              >
                {confirmDel === m.id ? 'Delete?' : '✕'}
              </span>
            </div>
          ))}
        </div>
      )}

      {preview && (
        <PreviewModal
          m={preview}
          creatives={creatives}
          onClose={() => setPreview(null)}
          onRename={(t) => void rename(preview.id, t)}
          onDelete={() => void del(preview.id)}
          onEdit={() => navigate(`/studio/create?media=${preview.source_media_id ?? preview.id}`)}
          onAttach={(cid) => void attachToPost(preview, cid)}
        />
      )}
    </div>
  );
}

/** Full-screen preview with the real actions a finished design should have. */
function PreviewModal({
  m,
  creatives,
  onClose,
  onRename,
  onDelete,
  onEdit,
  onAttach,
}: {
  m: MediaItem;
  creatives: Creative[];
  onClose: () => void;
  onRename: (title: string) => void;
  onDelete: () => void;
  onEdit: () => void;
  onAttach: (creativeId: string) => void;
}) {
  const [title, setTitle] = useState(m.title ?? '');
  const [picking, setPicking] = useState(false);
  const isReady = m.stage === 'ready';

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 300,
        background: 'rgba(0,0,0,.62)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'basilfade .15s ease',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          display: 'flex',
          gap: 20,
          maxWidth: 900,
          width: '100%',
          maxHeight: '90vh',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 30px 80px rgba(0,0,0,.6)',
        }}
      >
        {/* preview */}
        <div
          style={{
            flex: '1 1 55%',
            minWidth: 0,
            background: '#000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {m.kind === 'video' ? (
            <video src={m.url ?? ''} controls style={{ width: '100%', maxHeight: '90vh' }} />
          ) : (
            <img
              src={m.url ?? ''}
              alt={m.title ?? ''}
              style={{ width: '100%', maxHeight: '90vh', objectFit: 'contain', display: 'block' }}
            />
          )}
        </div>

        {/* actions */}
        <div
          style={{
            flex: '0 0 300px',
            padding: '22px 22px 18px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflowY: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
              {isReady ? 'FINISHED DESIGN' : m.kind === 'video' ? 'VIDEO CLIP' : 'PHOTO'}
            </div>
            <span onClick={onClose} style={{ cursor: 'pointer', color: C.muted, fontSize: 18 }}>
              ✕
            </span>
          </div>

          {/* rename */}
          <div>
            <div
              style={{
                font: `500 10px 'IBM Plex Mono',monospace`,
                color: C.faint,
                marginBottom: 6,
              }}
            >
              NAME
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Untitled"
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: C.surface2,
                  border: `1px solid ${C.line}`,
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 12.5,
                  color: C.primary,
                  fontFamily: 'inherit',
                }}
              />
              {title !== (m.title ?? '') && (
                <Button onClick={() => onRename(title.trim())} style={{ padding: '6px 11px' }}>
                  Save
                </Button>
              )}
            </div>
          </div>

          {/* actions */}
          {isReady &&
            (picking ? (
              <div>
                <div
                  style={{
                    font: `500 10px 'IBM Plex Mono',monospace`,
                    color: C.faint,
                    marginBottom: 6,
                  }}
                >
                  ATTACH TO WHICH POST?
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220 }}>
                  {creatives.length === 0 && (
                    <div style={{ fontSize: 12, color: C.muted }}>
                      No posts yet — write copy in Copy first.
                    </div>
                  )}
                  {creatives.map((cr) => (
                    <button
                      key={cr.id}
                      type="button"
                      onClick={() => onAttach(cr.id)}
                      style={{
                        textAlign: 'left',
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: `1px solid ${C.line}`,
                        background: C.surface2,
                        color: C.secondary,
                        fontSize: 12,
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                        lineHeight: 1.35,
                      }}
                    >
                      {cr.media_id ? '↻ ' : '＋ '}
                      {cr.primary_text?.slice(0, 60) || '(no caption)'}
                    </button>
                  ))}
                </div>
                <span
                  onClick={() => setPicking(false)}
                  style={{ fontSize: 11.5, color: C.muted, cursor: 'pointer', marginTop: 8 }}
                >
                  ← back
                </span>
              </div>
            ) : (
              <Button
                onClick={() => setPicking(true)}
                style={{ background: C.basil, color: C.basilInk, borderColor: C.basil }}
              >
                Use in a post →
              </Button>
            ))}

          {m.kind === 'image' && !picking && (
            <ActionRow label="✦ Edit in Compose" onClick={onEdit} />
          )}
          {!picking && m.url && (
            <a
              href={m.url}
              download
              target="_blank"
              rel="noreferrer"
              style={{
                textAlign: 'center',
                padding: '9px 14px',
                borderRadius: 9,
                border: `1px solid ${C.line}`,
                background: C.surface2,
                color: C.secondary,
                fontSize: 12.5,
                textDecoration: 'none',
              }}
            >
              ↓ Download
            </a>
          )}

          {!picking && (
            <span
              onClick={onDelete}
              style={{ marginTop: 'auto', color: C.act, fontSize: 12.5, cursor: 'pointer' }}
            >
              Delete
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ActionRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'center',
        padding: '9px 14px',
        borderRadius: 9,
        border: `1px solid ${C.line}`,
        background: C.surface2,
        color: C.secondary,
        fontSize: 12.5,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/** Thumbnail that fades in on load (no more black-box flash). */
function Thumb({ m, height }: { m: MediaItem; height: number }) {
  const [loaded, setLoaded] = useState(false);
  const common: React.CSSProperties = {
    width: '100%',
    height,
    objectFit: 'cover',
    display: 'block',
    opacity: loaded ? 1 : 0,
    transition: 'opacity .25s ease',
  };
  return (
    <div
      style={{
        position: 'relative',
        height,
        background: C.surface2,
        animation: loaded ? undefined : 'basilpulse 1.2s ease-in-out infinite',
      }}
    >
      {m.kind === 'video' ? (
        <>
          {m.url && (
            <video
              src={m.url}
              muted
              preload="metadata"
              onLoadedData={() => setLoaded(true)}
              style={common}
            />
          )}
          <span
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 26,
              color: 'rgba(255,255,255,.85)',
              pointerEvents: 'none',
            }}
          >
            ▶
          </span>
        </>
      ) : (
        m.url && (
          <img
            src={m.url}
            alt={m.alt ?? m.title ?? ''}
            onLoad={() => setLoaded(true)}
            style={common}
          />
        )
      )}
    </div>
  );
}

function Tab({
  active,
  onClick,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '7px 14px',
        borderRadius: 9,
        cursor: 'pointer',
        fontFamily: 'inherit',
        fontSize: 13,
        border: `1px solid ${active ? C.basil : C.line}`,
        background: active ? C.surface2 : 'transparent',
        color: active ? C.primary : C.muted,
      }}
    >
      {label}
      <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>{count}</span>
    </button>
  );
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        position: 'absolute',
        top: 6,
        left: 6,
        padding: '2px 7px',
        borderRadius: 6,
        background: 'rgba(0,0,0,.6)',
        color: '#fff',
        font: `500 9px 'IBM Plex Mono',monospace`,
        letterSpacing: '.08em',
        pointerEvents: 'none',
      }}
    >
      {children}
    </span>
  );
}

function GoogleDriveMark() {
  return (
    <svg width="15" height="13" viewBox="0 0 87.3 78" aria-hidden="true">
      <path
        fill="#0066da"
        d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
      />
      <path
        fill="#00ac47"
        d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 48.4C.4 49.8 0 51.35 0 52.9h27.5z"
      />
      <path
        fill="#ea4335"
        d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57.4c.8-1.4 1.2-2.95 1.2-4.5H59.798l5.852 11.5z"
      />
      <path
        fill="#00832d"
        d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z"
      />
      <path
        fill="#2684fc"
        d="M59.8 52.9H27.5L13.75 76.7c1.35.8 2.9 1.3 4.5 1.3h50.8c1.6 0 3.15-.5 4.5-1.3z"
      />
      <path
        fill="#ffba00"
        d="M73.4 26.5L60.75 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 27.9h27.45c0-1.55-.4-3.1-1.2-4.5z"
      />
    </svg>
  );
}
